import { z } from "zod/v4";

import { StripeProductVersionList } from "../schemas/stripe";

import { AgencyCustomerDtoSchema } from "./agency-customers";
import { FullOrganizationAgencyLinkDtoSchema } from "./organization-agency-links";
import { OrgIdNameDtoSchema } from "./organization-name-id";
import { StripeSubscriptionStatusList } from "./organization-subscriptions";
import { StripeSubscriptionLinkDtoSchema } from "./stripe-subscription-links";

export const AgencyDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  internalNotes: z.string().nullish(),
  billing: z.boolean().nullish(),
});

export type AgencyDto = z.infer<typeof AgencyDtoSchema>;

export const CreateAgencyDtoSchema = AgencyDtoSchema.extend({
  id: z.uuid().nullish(),
});

export type CreateAgencyDto = z.infer<typeof CreateAgencyDtoSchema>;

export const AgencyProductDtoSchema = z.object({
  stripeProductId: z.string(),
  name: z.string(),
  price: z.number(),
  orgs: z
    .object({
      orgId: z.uuid(),
      name: z.string(),
    })
    .array(),
});

export const AgencySubscriptionProductOrgDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  meterValue: z.number(),
});

export type AgencySubscriptionProductOrgDto = z.infer<
  typeof AgencySubscriptionProductOrgDtoSchema
>;

export const AgencySubscriptionProductLinkDtoSchema = z.object({
  id: z.uuid(),
  stripeProductId: z.string(),
  stripeSubscriptionLinkId: z.string(),
  organization: OrgIdNameDtoSchema,
  meterValue: z.number(),
});
export type AgencySubscriptionProductLinkDto = z.infer<
  typeof AgencySubscriptionProductLinkDtoSchema
>;

export const AgencySubscriptionItemDtoSchema = z.object({
  stripeProductLinks: AgencySubscriptionProductLinkDtoSchema.array(),
  stripeSubscriptionItemId: z.string(),
  price: z.object({
    stripePriceId: z.string(),
  }),
  product: z.object({
    name: z.string(),
    stripeProductId: z.string(),
  }),
  meter: z.object({
    totalMeterValue: z.number(),
    meterUnit: z.string(),
    totalMeterCost: z.number(),
    meter: z.object({ id: z.string(), display_name: z.string() }).nullish(),
  }),
});

export type AgencySubscriptionItemDto = z.infer<
  typeof AgencySubscriptionItemDtoSchema
>;

export const AgencySubscriptionDtoSchema = z.object({
  status: z.enum(StripeSubscriptionStatusList),
  stripeSubscriptionId: z.string(),
  stripeSubscriptionScheduleId: z.string().nullable(),
  stripeCustomerId: z.string(),
  meter: z.any().nullish(),
  version: z.enum(StripeProductVersionList),
  items: AgencySubscriptionItemDtoSchema.array(),
  stripeSubscriptionLinks: StripeSubscriptionLinkDtoSchema.array(),
});

export type AgencySubscriptionDto = z.infer<typeof AgencySubscriptionDtoSchema>;

export type AgencyProductDto = z.infer<typeof AgencyProductDtoSchema>;

export const FullAgencyDtoSchema = z.object({
  id: z.uuid(),
  // subscriptions tab:
  subscriptions: AgencySubscriptionDtoSchema.array(),
  // customers tab:
  customers: AgencyCustomerDtoSchema.array(),
  // organizations tab:
  organizationAgencyLinks: FullOrganizationAgencyLinkDtoSchema.array(),
});

export type FullAgencyDto = z.infer<typeof FullAgencyDtoSchema>;
